import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import NewsletterSignUp from '../../common/Newsletter-sign';
import heroImage from '../../../assets/christmas25/grotto.webp';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';

const Page = styled.div`
  min-height: 100vh;
  padding-top: 120px;
  background-color: ${props => props.theme.colors.offWhite};
`;

const EventHero = styled.div`
  width: 100%;
  height: 420px;
  background-image: ${props => `linear-gradient(to bottom, rgba(0,0,0,0.25), rgba(0,0,0,0.65)), url(${props.image})`};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  position: relative;

  @media (max-width: 1024px) { height: 420px; }
  @media (max-width: 768px) { height: 340px; }
`;

const HeroContent = styled.div`
  width: 100%;
  max-width: 1400px;
  padding: 3rem 2rem;
  color: white;
`;

const Badge = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 3rem;
  font-weight: 700;
  margin: 0.25rem 0 0.5rem;
  line-height: 1.2;
  text-shadow: 0 2px 6px rgba(0,0,0,0.35);
`;

const Strapline = styled.p`
  font-size: 1.25rem;
  margin: 0;
  opacity: 0.95;
`;

const Content = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 1.5rem auto 0;
  padding: 0 2rem 3rem;
`;

const OptionsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.div.attrs({ className: 'bf-surface' })`
  background: white;
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  z-index: 1;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0,0,0,0.12);
  }
`;

const OptionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
  position: relative;
  padding-bottom: 0.5rem;

  &::after {
    content: '';
    position: absolute;
    left: 0; bottom: 0;
    width: 80px; height: 3px;
    background: ${props => props.theme.colors.accent};
  }
`;

const OptionDescription = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #333;
  margin-bottom: 1.5rem;
  flex-grow: 1;

  p {
    margin: 0 0 1rem;
  }

  ul {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }

  li {
    margin: 0.5rem 0;
  }
`;

const LocationBadge = styled.div`
  display: inline-block;
  background: rgba(157, 109, 73, 0.1);
  border: 1px solid rgba(157, 109, 73, 0.3);
  border-radius: 6px;
  padding: 0.5rem 1rem;
  margin-bottom: 1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  font-size: 0.95rem;
`;

const CTAButton = styled.a.attrs({ className: 'cta-button' })`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.9rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 700;
  margin-top: auto;
  text-align: center;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.95;
    transform: translateY(-2px);
  }
`;

const SantasGrottoRetailVillageOptions = () => {
  const { isActive: isBlackFriday } = useBlackFriday();
  const pageClassName = isBlackFriday ? 'christmas-page christmas-page--black-friday' : 'christmas-page';
  const meta = {
    title: "Santa's Grotto at Belvoir Retail Village - Booking Options | Christmas Events",
    description: "Choose your perfect Christmas experience: Visit Santa's Grotto only at Belvoir Retail Village, or make it a whole day out with A Magical Night Before Christmas at Belvoir Castle with Mice Trail. Book now!",
    canonical: 'https://www.belvoircastle.com/christmas/santas-grotto-retail-village-options'
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: "Santa's Grotto at Belvoir Retail Village - Booking Options",
    description: meta.description,
    url: meta.canonical
  };

  return (
    <Page className={pageClassName}>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="keywords" content="Santa's Grotto, Christmas activities Leicestershire, Christmas family events, Belvoir Castle Christmas, Christmas day out, Santa visit, Christmas grotto, Belvoir Retail Village" />
        <meta name="author" content="Belvoir Castle" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href={meta.canonical} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:image" content="https://www.belvoircastle.com/christmas25/grotto.webp" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={meta.canonical} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content="https://www.belvoircastle.com/christmas25/grotto.webp" />
        
        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        
        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.belvoircastle.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Christmas 2025",
                "item": "https://www.belvoircastle.com/christmas"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "Santa's Grotto at Retail Village Options",
                "item": meta.canonical
              }
            ]
          })}
        </script>
      </Helmet>

      <EventHero image={heroImage}>
        <HeroContent>
          <Badge>Choose Your Booking Option</Badge>
          <Title>Santa's Grotto at Belvoir Retail Village</Title>
          <Strapline>Grotto only or make it a whole day out</Strapline>
        </HeroContent>
      </EventHero>

      <Content>
        <OptionsContainer>
          <OptionCard>
            <OptionTitle>Grotto Only</OptionTitle>
            <LocationBadge>📍 At Belvoir Retail Village</LocationBadge>
            <OptionDescription>
              <p><strong>Visit Santa's Grotto at Belvoir Retail Village</strong></p>
              <p>Visit Santa in his magical grotto at Belvoir Retail Village. This option includes:</p>
              <ul>
                <li>Private family visit with Santa</li>
                <li>A special present for your child</li>
                <li>Photo opportunities in a beautifully decorated festive setting</li>
                <li>Explore the Retail Village shops and dining options</li>
              </ul>
              <p><strong>Available:</strong> 20th-23rd December 2025</p>
              <p><strong>Location:</strong> Belvoir Retail Village (just a 5 minute walk from Belvoir Castle)</p>
              <p>Perfect for families looking for a Santa visit without full Castle entry.</p>
            </OptionDescription>
            <CTAButton href="/booking-journey?locationId=978&pageId=9afa8eb9-dd32-4529-b9af-316e13a7397e">
              Book Grotto Only
            </CTAButton>
          </OptionCard>

          <OptionCard>
            <OptionTitle>Make It a Whole Day Out</OptionTitle>
            <LocationBadge>📍 Castle Entry + Grotto at Retail Village</LocationBadge>
            <OptionDescription>
              <p><strong>A Magical Night Before Christmas at Belvoir Castle with Mice Trail</strong></p>
              <p>Make it a complete Christmas experience! Book Castle entry and add Santa's Grotto at Belvoir Retail Village. Your whole day out includes:</p>
              <ul>
                <li>Entry to Belvoir Castle</li>
                <li>Explore the beautifully decorated Castle rooms</li>
                <li>Enjoy the Festive Mice Trail (included with entry)</li>
                <li>Add Santa's Grotto at Belvoir Retail Village (available 20th-23rd December)</li>
                <li>Add Festive Afternoon Tea for a special treat</li>
                <li>Full day access to explore the estate</li>
              </ul>
              <p><strong>How to book:</strong> Select your Castle entry tickets first, then add Santa's Grotto at Belvoir Retail Village and any other extras during checkout.</p>
            </OptionDescription>
            <CTAButton href="/booking-journey?locationId=978&pageId=278ef0c8-a65b-4e31-9726-305ae06a5a14">
              Book Whole Day Out
            </CTAButton>
          </OptionCard>
        </OptionsContainer>
      </Content>

      <div style={{ margin: '3rem 0' }}>
        <NewsletterSignUp tag="Christmas 2025" />
      </div>
    </Page>
  );
};

export default SantasGrottoRetailVillageOptions;

